import { gql } from '@apollo/client';

export const GET_PET_PARENTS = gql`
  query GetPetParentsTable(
    $onboardingStatus: String,
    $courseStatus: String,
    $animalType: [String],
    $careType: String,
    $daysAgo: Int,
    $smsStatus: String,
    $interests: String,
    $offset: Int,
    $limit: Int,
    $order: String,
    $orderBy: String,
    $searchText: String,
    $searchField: String,
    $onlyGroupByPetParent: Boolean,
  ) {
    petParents: petParents(
      onboardingStatus: $onboardingStatus,
      courseStatus: $courseStatus,
      animalType: $animalType,
      careType: $careType,
      daysAgo: $daysAgo,
      smsStatus: $smsStatus,
      interests: $interests,
      offset: $offset,
      limit: $limit,
      order: $order,
      orderBy: $orderBy,
      searchText: $searchText,
      searchField: $searchField,
      onlyGroupByPetParent: $onlyGroupByPetParent,
    ) {
      id
      firstName
      lastName
      email
      phoneNumber
      dateCreated
      mailchimpSyncDate
      onboardDate
      hardBounceDate
      hardBounceResolvedDate
      thinkificId
      smsSyncDate
      smsOptOutDate
      name
      animalType
      careType
      caseNumber
      activatedAt
      completedAt
      animalCount
      interests
    }
    petParentCount: petParentCount(
      onboardingStatus: $onboardingStatus,
      courseStatus: $courseStatus,
      animalType: $animalType,
      careType: $careType,
      daysAgo: $daysAgo,
      smsStatus: $smsStatus,
      interests: $interests,
      searchText: $searchText,
      searchField: $searchField
      onlyGroupByPetParent: $onlyGroupByPetParent,
    ) {
      count
    }
  }
`;

export const GET_AWOS = gql`
  query GetAwos(
    $offset: Int,
    $limit: Int,
    $order: String,
    $orderBy: String,
    $filters: AwoSearchInput
  ) {
    awos: awos(
      offset: $offset,
      limit: $limit,
      order: $order,
      orderBy: $orderBy,
      filters: $filters
    ) {
      id
      name
      displayName
      city
      state
      country
      website
      membershipType
      smsName
      kittenFoster
      catFoster
      dogFoster
      catAdopt
      dogAdopt
      members
      status
      lastSyncDate
    }
    awosCount: awosCount(filters: $filters){
      count
    }
  }
`;

export const GET_AWO = gql`
  query GetAwo {
    awo {
      id
      dateCreated
      name
      displayName
      smsName
      kittenFoster
      catFoster
      dogFoster
      catAdopt
      dogAdopt
      twilioPhoneNumber
      preSurrenderDashboard
      country
      resourcesUrl
      membershipType
      automationEnabled
      timezone
      taskSettings {
        id
        feedback1
        feedback2
        feedback3
        feedback4
        feedback5
        asksQuestion
        emailTypo
        phoneTypo
        adoptionReview
        fosterReview
        unresponsive
        completedSupport
      }
    }
  }
`;

export const GET_AWO_WITH_FLYERS = gql`
  query GetAwoWithFlyers {
    awo {
      id
      dateCreated
      name
      displayName
      smsName
      kittenFoster
      catFoster
      dogFoster
      catAdopt
      dogAdopt
      twilioPhoneNumber
      preSurrenderDashboard
      country
      resourcesUrl
      membershipType
      automationEnabled
      timezone
      taskSettings {
        id
        feedback1
        feedback2
        feedback3
        feedback4
        feedback5
        asksQuestion
        emailTypo
        phoneTypo
        adoptionReview
        fosterReview
        unresponsive
        completedSupport
      }
      awoFlyers {
        pdfMonkeyUrl {
          url
        }
      }
    }
  }
`;

export const GET_SINGLE_AWO = gql`
  query GetSingleAwo($id: Int) {
    awo(id: $id) {
      id
      name
      smsName
      kittenFoster
      catFoster
      dogFoster
      catAdopt
      dogAdopt
      twilioPhoneNumber
      preSurrenderDashboard
      membershipType
      displayName
      note
      website
      streetAddress
      city
      state
      zipCode
      country
      timezone
      orgType
      resourcesUrl
      automationEnabled
      status
      failureReason
      lastSyncDate
      dateUpdated
      animalsUpdated
      adoptionPacketLinkCats
      adoptionPacketLinkDogs
      donationLink
      faqLink
      orgPhoneNumber
      externalOrgName
      externalOrgId
      adoptAPetId
      adoptAPetEnabled
      petszelPartner
      behaviorEscalationContact
      fosterContact
      adopterContact
      preSurrenderContact
      primaryAdminId
      primaryAdmin {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const GET_AWO_USERS = gql`
  query GetAwoUsers(
    $awoId: Int!,
    $order: String,
    $orderBy: String,
    $name: String,
    $onlyReviewers: Boolean,
  ) {
    awoUsers(awoId: $awoId, order: $order, orderBy: $orderBy, name: $name, onlyReviewers: $onlyReviewers) {
      id
      awoId
      firstName
      lastName
      email
      inviteDate
      lastLogin
      lastTaskAction
      adoptionNotificationEmail
      fosterNotificationEmail
      preSurrenderNotificationEmail
      dogNotificationEmail
      catNotificationEmail
      taskNotificationsEmail
    }
  }
`;

export const GET_ANIMALS = gql`
  query GetAnimals($awoId: Int) {
    animals(awoId: $awoId) {
      id
      name
      awoName
      animalMedias {
        id
        fileType
        url
        isPrimary
      }
    }
  }
`;

export const GET_COURSE_STATS = gql`
  query GetCourseStats(
    $animalType: String,
    $careType: String,
    $startDaysAgo: Int,
    $midDaysAgo: Int,
    $endDaysAgo: Int
  ) {
    current: courseStats(
      animalType: $animalType,
      careType: $careType,
      startDaysAgo: $midDaysAgo,
      endDaysAgo: $endDaysAgo
    ) {
      invitedCount
      onboardedCount
    }
    previous: courseStats(
      animalType: $animalType,
      careType: $careType,
      startDaysAgo: $startDaysAgo,
      endDaysAgo: $midDaysAgo
    ) {
      invitedCount
      onboardedCount
    }
  }
`;

export const GET_TYPEFORM_PET_PARENT = gql`
  query GetTypeformPetParent {
    petParent {
      id
      email
      awo {
        id
        name
        donationLink
      }
      animals {
        id
        name
        careType
        animalType
      }
    }
  }
`;

export const GET_HUB_PET_PARENT = gql`
  query GetHubPetParent {
    petParent {
      id
      primaryAnimalId
      email
      firstName
      lastName
      phoneNumber
      streetAddress
      state
      country
      startedDaysAgo
      numThinkificLogins
      numHubLogins
      smsPreferred
      membershipStatus
      membershipExpiryDate
      membershipPlanName
      isPremium
      checklistCompletedTasks
      bookmarkedLessons
      awo {
        id
        name
        displayName
        membershipType
        twilioPhoneNumber
        donationLink
        adoptionPacketLinkDogs
        adoptionPacketLinkCats
        faqLink
        smsName
        country
      }
      animals {
        id
        name
        animalType
        careType
        gender
        primaryBreed
        birthDate
        exitDate
        externalId
        caseNumber
        microchipNumber
        awoId
        surrenderReasons
        awo {
          name
          displayName
        }
        animalMedias {
          id
          dateCreated
          fileType
          url
          isPrimary
        }
      }
      orders {
        intervalCount
        productId
        product {
          name
        }
      }
    }
  }
`;

export const SET_CHECKLIST_TASK = gql`
  mutation SetChecklistTask($taskKey: String!, $completed: Boolean!) {
    setChecklistTask(taskKey: $taskKey, completed: $completed)
  }
`;

export const SET_LESSON_BOOKMARK = gql`
  mutation SetLessonBookmark($slug: String!, $bookmarked: Boolean!) {
    setLessonBookmark(slug: $slug, bookmarked: $bookmarked)
  }
`;

const ENGAGEMENT_REPORT_FIELDS = `
  totals {
    supported
    engaged
    lessons
    messages
    surveys
    engagedLessons
    engagedMessages
    engagedSurveys
  }
  byRelationship {
    careType
    animalType
    supported
    engaged
    lessons
    messages
    surveys
  }
`;

// Engagement tab (ticket-2542): the selected period and the one right before it. All time
// skips `previous` — it has nothing to compare against.
export const GET_ENGAGEMENT_REPORT = gql`
  query GetEngagementReport(
    $startDaysAgo: Int, $endDaysAgo: Int, $previousStartDaysAgo: Int, $previousEndDaysAgo: Int, $allTime: Boolean!
  ) {
    current: engagementReport(startDaysAgo: $startDaysAgo, endDaysAgo: $endDaysAgo) {
      ${ENGAGEMENT_REPORT_FIELDS}
    }
    previous: engagementReport(startDaysAgo: $previousStartDaysAgo, endDaysAgo: $previousEndDaysAgo)
      @skip(if: $allTime) {
      ${ENGAGEMENT_REPORT_FIELDS}
    }
  }
`;

export const RECORD_LESSON_VIEW = gql`
  mutation RecordLessonView($slug: String!, $source: String!) {
    recordLessonView(slug: $slug, locked: false, source: $source)
  }
`;

export const GET_ANIMAL_MEDIA_DOWNLOAD_URLS = gql`
  query GetAnimalMediaDownloadUrls($animalId: Int!) {
    animalMediaDownloadUrls(animalId: $animalId) {
      pdfUrl
      awsUrl
    }
  }
`;

export const GET_PET_PARENT = gql`
  query GetPetParent($id: Int!, $includePdfMonkeyUrls: Boolean!) {
    petParent(id: $id) {
      id
      firstName
      lastName
      email
      phoneNumber
      city
      country
      state
      streetAddress
      zipCode
      smsOptOutDate
      unsubscribeDate
      purchaseDate
      thinkificId
      numThinkificLogins
      lastLoginDate
      smsPreferred
      startedDaysAgo
      dateStarted
      dateCancelled
      activeAuthToken {
        id
        token
        expirationDate
      }
      animalMedias {
        id
        dateCreated
        fileType
        url
        hidden
      }
      awo {
        id
        name
        membershipType
        smsName
      }
      animals {
        id
        dateCreated
        name
        animalType
        careType
        gender
        primaryBreed
        birthDate
        exitDate
        caseNumber
        externalId
        microchipNumber
        awo {
          id
          name
          preSurrenderDashboard
          smsName
        }
        animalMedias {
          id
          dateCreated
          fileType
          url
          pdfMonkeyUrl @include(if: $includePdfMonkeyUrls) {
            url
          }
          isPrimary
          hidden
        }
      }
      enrollments {
        courseName
      }
      orders {
        intervalCount
        productId
        product {
          name
        }
      }
      activeConversation {
        id
        isActive
        dateCreated
        animal {
          name
          careType
          exitDate
        }
      }
    }
  }
`;

export const GET_TYPEFORM_SINGULAR = gql`
  query GetTypeformForm($id: String!) {
    typeformForm(id: $id) {
      id
      title
      createdAt
      lastUpdatedAt
      fields {
        id
        title
      }
    }
  }
`;

export const GET_TYPEFORM = gql`
  query GetTypeformForm($id: String!, $query: String!) {
    typeformForm: typeformForm(id: $id) {
      fields {
        id
        title
      }
    }
    typeformResponse: typeformResponse(id: $id, query: $query) {
      items {
        answers {
          ... on NumberField {
            field {
              id
              type
            }
            number
            type
          }
          ... on ChoicesField {
            field {
              id
              type
            }
            choices {
              labels
            }
            type
          }
          ... on ChoiceField {
            field {
              type
              id
            }
            choice {
              label
            }
            type
          }
          ... on BooleanField {
            field {
              id
              type
            }
            boolean
            type
          }
        }
        hidden {
          awo
          country
          name
          email
          onboarded
          referral
        }
      }
    }
  }
`;

export const GET_TYPEFORM_FORMS = gql`
  query GetTypeformForms {
    typeformForms {
      id
      typeformId
      createdAt
      lastUpdatedAt
      typeformTitle
      responseTitle
      latestVersion
      questions {
        id
        title
        fieldType
      }
    }
  }
`;

export const ADD_TYPEFORM_FORM = gql`
  mutation AddTypeformForm($id: String!, $title: String!) {
    addTypeformForm(id: $id, title: $title)
  }
`;

export const UPDATE_TASK = gql`
  mutation UpdateTask($id: String!, $completed: Boolean!) {
    updateTask(id: $id, completed: $completed)
  }
`;

export const UPDATE_EMAIL = gql`
  mutation UpdateEmail($oldEmail: String!, $newEmail: String!) {
    updateEmail(oldEmail: $oldEmail, newEmail: $newEmail)
  }
`;

export const UPDATE_PHONE_NUMBER = gql`
  mutation UpdatePhoneNumber($email: String!, $newPhone: String!) {
    updatePhoneNumber(email: $email, newPhone: $newPhone)
  }
`;

export const UPDATE_PET_PARENT_PROFILE = gql`
  mutation UpdatePetParentProfile(
    $id: ID!
    $firstName: String
    $lastName: String
    $email: String
    $phoneNumber: String
    $smsPreferred: Boolean
  ) {
    updatePetParentProfile(
      id: $id
      firstName: $firstName
      lastName: $lastName
      email: $email
      phoneNumber: $phoneNumber
      smsPreferred: $smsPreferred
    ) {
      id
      firstName
      lastName
      email
      phoneNumber
      smsPreferred
    }
  }
`;

export const UPDATE_PET_PARENT_ANIMAL = gql`
  mutation UpdatePetParentAnimal($oldData: JSON!, $newData: JSON!) {
    updatePetParentAnimal(oldData: $oldData, newData: $newData) {
      id
      firstName
      lastName
      email
      phoneNumber
      state
      country
      streetAddress
      animals {
        id
        name
        careType
        animalType
        primaryBreed
        gender
        birthDate
        exitDate
        caseNumber
      }
    }
  }
`;

export const STOP_SUPPORT_UNSUBSCRIBE = gql`
  mutation StopSupportUnsubscribe($email: String!, $actionerEmail: String!, $action: String!, $newStartDate: String) {
    stopSupportUnsubscribe(email: $email, actionerEmail: $actionerEmail, action: $action, newStartDate: $newStartDate)
  }
`;

export const SEND_MAGIC_LINK_EMAIL = gql`
  mutation SendMagicLinkEmail($email: String!) {
    sendMagicLinkEmail(email: $email)
  }
`;

export const SEND_PASSWORD_CREATED_EMAIL = gql`
  mutation SendPasswordCreatedEmail($email: String!) {
    sendPasswordCreatedEmail(email: $email)
  }
`;

export const GET_FILTERED_TYPEFORM_RESPONSES = gql`
  query GetFilteredTypeformResponses(
    $ppId: String,
    $typeformIds: [String!],
    $awoId: String,
    $limit: Int,
    $offset: Int,
    $orderBy: String,
    $daysAgo: Int,
    $careType: String,
    $animalType: String,
    $excludeSkipTaskCreation: Boolean = false
  ) {
    responses: response(
      ppId: $ppId,
      typeformIds: $typeformIds,
      awoId: $awoId, limit: $limit, offset: $offset,
      orderBy: $orderBy, daysAgo: $daysAgo, careType: $careType, animalType: $animalType,
      excludeSkipTaskCreation: $excludeSkipTaskCreation) {
      id
      typeformId
      submittedAt
      title
      petParent {
        id
        email
        firstName
        lastName
        animals {
          id
          name
          animalType
          careType
        }
      }
      animal {
        id
        name
        animalType
        careType
      }
      answers {
        id
        text
        question
      }
    }
  }
`;

export const GET_TYPEFORM_RESPONSES = gql`
  query GetTypeformResponses($ppId: String!) {
    responses: response(ppId: $ppId, orderBy: "asc", excludeSkipTaskCreation: true) {
      id
      typeformId
      submittedAt
      title
      answers {
        id
        text
        question
      }
    }
    questions: questions(ppId: $ppId) {
      id
      dateUpdated
      comments {
        id
        authorId
        htmlBody
        medias {
          url
          fileType
        }
      }
    },
    updates: update(ppId: $ppId) {
      id
      dateUpdated
      title
      subtitle
      answers {
        id
        text
        question
      }
    }
  }
`;

export const GET_TICKET = gql`
  query GetTicket($search: TicketSearchInput) {
    question(search: $search) {
      id
      ticketId
      subject
      dateUpdated
      reviewDate
      flagDate
      petParent {
        id
        firstName
      }
      comments {
        id
        authorId
        htmlBody
        medias {
          url
          fileType
        }
      }
      tags {
        id
        value
        deleted
      }
      reviewedBy {
        id
        firstName
      }
    }
    nextQuestion(search: $search) {
      ticketId
    }
    questionCount: questionCount(search: $search) {
      count
    }
  }
`;

export const GET_PRE_SURRENDER_DASHBOARD_STATS = gql`
  query GetPreSurrenderDashboardStats($careType: String, $startDaysAgo: Int, $endDaysAgo: Int) {
    taskCount: taskCount(completed: false, careType: $careType, startDaysAgo: $startDaysAgo, endDaysAgo: $endDaysAgo) {
      count
    }
    totalSupportStats: supportStats(startDaysAgo: 30, endDaysAgo: 0) {
      invitedCount
      optOutCount
      responsiveCount
    }
    totalPreviousMonthSupportStats: supportStats(startDaysAgo: 60, endDaysAgo: 30) {
      invitedCount
      optOutCount
      responsiveCount
    }
    dogSupportStats: supportStats(animalType: "Dog", startDaysAgo: 30, endDaysAgo: 0) {
      invitedCount
    }
    previousMonthDogSupportStats: supportStats(animalType: "Dog", startDaysAgo: 60, endDaysAgo: 30) {
      invitedCount
    }
    catSupportStats: supportStats(animalType: "Cat", startDaysAgo: 30, endDaysAgo: 0) {
      invitedCount
    }
    previousMonthCatSupportStats: supportStats(animalType: "Cat", startDaysAgo: 60, endDaysAgo: 30) {
      invitedCount
    }
    currentMonthEngagementStats: engagementStats(startDaysAgo: 30, endDaysAgo: 0) {
      totalEngaged
    }
    previousMonthEngagementStats: engagementStats(startDaysAgo: 60, endDaysAgo: 30) {
      totalEngaged
    }
  }
`;

export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($careType: String, $species: String, $startDaysAgo: Int, $endDaysAgo: Int) {
    taskCount: taskCount(
      completed: false, careType: $careType, species: $species,
      startDaysAgo: $startDaysAgo, endDaysAgo: $endDaysAgo,
    ) {
      count
    }
    currentMonthEngagementStats: engagementStats(startDaysAgo: 60, endDaysAgo: 30) {
      adoptersEngaged
      fostersEngaged
    }
    previousMonthEngagementStats: engagementStats(startDaysAgo: 90, endDaysAgo: 60) {
      adoptersEngaged
      fostersEngaged
    }
    supportStats: supportStats(startDaysAgo: 30, endDaysAgo: 0) {
      invitedCount
      optOutCount
    }
    previousMonthSupportStats: supportStats(startDaysAgo: 60, endDaysAgo: 30) {
      invitedCount
      optOutCount
    }
    nps {
      score
    }
  }
`;

export const GET_DASHBOARD = gql`
  query GetDashboard {
    taskCount: taskCount(completed: false) {
      count
    }
    newsfeedCount {
      count
    }
    awoUser {
      email
      adoptionNotificationEmail
      fosterNotificationEmail
      preSurrenderNotificationEmail
    }
  }
`;

export const GET_NEWSFEED = gql`
  query GetNewsfeed($offset: Int!, $limit: Int!) {
    newsfeed(offset: $offset, limit: $limit) {
      id
      dateUpdated
      actionText
      petParent {
        id
        firstName
        lastName
        animals {
          name
          careType
          dateCreated
          animalType
        }
      }
      question {
        id
      }
    }
  }
`;

export const GET_TASKS = gql`
  query GetTasks(
    $offset: Int!,
    $limit: Int!,
    $completed: Boolean,
    $startDaysAgo: Int,
    $endDaysAgo: Int,
    $careType: String,
    $species: String,
  ) {
    tasks: tasks(
      offset:$offset,
      limit: $limit,
      completed: $completed,
      startDaysAgo: $startDaysAgo,
      endDaysAgo: $endDaysAgo,
      careType: $careType,
      species: $species,
    ) {
      id
      dateUpdated
      dateCreated
      actionText
      nextStep
      completed
      petParent {
        id
        firstName
        lastName
        email
        animals {
          name
          animalType
          careType
          dateCreated
        }
      }
    }
    taskCount: taskCount(
      completed: $completed,
      startDaysAgo: $startDaysAgo,
      endDaysAgo: $endDaysAgo,
      careType: $careType,
      species: $species,
    ) {
      count
    }
  }
`;

export const GET_BEHAVIORAL_ISSUES = gql`
  query GetFeedback($feedbackDaysAgo: Int) {
    awoAdoptedDogBehavioralIssues: behavioralIssues(
      typeformFormId: 46,
      careType: "Adopted",
      invert: 0,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    otherAdoptedDogBehavioralIssues: behavioralIssues(
      typeformFormId: 46,
      careType: "Adopted",
      invert: 1,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    awoFosterDogBehavioralIssues: behavioralIssues(
      typeformFormId: 46,
      careType: "Foster",
      invert: 0,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    otherFosterDogBehavioralIssues: behavioralIssues(
      typeformFormId: 46,
      careType: "Foster",
      invert: 1,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    awoAdoptedCatBehavioralIssues: behavioralIssues(
      typeformFormId: 47,
      careType: "Adopted",
      invert: 0,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    otherAdoptedCatBehavioralIssues: behavioralIssues(
      typeformFormId: 47,
      careType: "Adopted",
      invert: 1,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    awoFosterCatBehavioralIssues: behavioralIssues(
      typeformFormId: 47,
      careType: "Foster",
      invert: 0,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    otherFosterCatBehavioralIssues: behavioralIssues(
      typeformFormId: 47,
      careType: "Foster",
      invert: 1,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
  }
`;

export const GET_FEEDBACK = gql`
  query GetFeedback {
    taskCount(completed: false) {
      count
    }
    behavioralIssues {
      numResponses
    }
  }
`;

export const GET_CAMPAIGNS = gql`
  query GetCampaigns {
    campaigns {
      id
      name
      displayName
      sendHour
      messageTemplates {
        id
        campaignId
        text
        position
        sendDelay
        note
        numericalResponse
      }
      vMessageTemplate @client
    }
  }
`;

export const GET_AWO_REPORTS = gql`
  query GetAwoReports($offset: Int, $limit: Int, $order: String, $orderBy: String) {
    awoReports(offset: $offset, limit: $limit, order: $order, orderBy: $orderBy) {
      id
      dateCreated
      documentTitle
      documentId
      pdfMonkeyUrl {
        url
      }
    }
    awoReportsCount {
      count
    }
  }
`;

export const GET_TICKETS_TO_REVIEW = gql`
  query GetTicketsToReview {
    ticketsToReview {
      tagName
      unreviewedCount
      firstQuestion {
        ticketId
      }
    }
    questionCount {
      count
    }
  }
`;

export const CREATE_AWO = gql`
  mutation CreateAwo($awo: AwoInput!) {
    createAwo(awo: $awo) {
      code
      success
      message
      awo {
        id
        name
      }
      awoUser {
        id
      }
    }
  }
`;

export const UPDATE_AWO = gql`
  mutation UpdateAwo($id: ID!, $awo: AwoInput!) {
    updateAwo(id: $id, awo: $awo) {
      code
      success
      message
      awo {
        id
        name
        smsName
        kittenFoster
        catFoster
        dogFoster
        catAdopt
        dogAdopt
        twilioPhoneNumber
        preSurrenderDashboard
        membershipType
        displayName
        note
        website
        city
        state
        zipCode
        country
        timezone
        orgType
        resourcesUrl
        status
        failureReason
        lastSyncDate
        animalsUpdated
        dateUpdated
        externalOrgName
        externalOrgId
        orgPhoneNumber
        donationLink
        adoptionPacketLinkDogs
        adoptionPacketLinkCats
        faqLink
        adoptAPetId
        adoptAPetEnabled
        petszelPartner
        behaviorEscalationContact
        fosterContact
        adopterContact
        preSurrenderContact
        primaryAdminId
        primaryAdmin {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const CREATE_AWO_USER = gql`
  mutation CreateAwoUser($awoUser: AwoUserInput!) {
    response: createAwoUser(awoUser: $awoUser) {
      code
      success
      message
      awoUser {
        id
      }
    }
  }
`;

export const UPDATE_AWO_USER = gql`
  mutation UpdateAwoUser($id: String!, $awoUser: AwoUserInput!) {
    response: updateAwoUser(id: $id, awoUser: $awoUser) {
      code
      success
      message
      awoUser {
        id
        email
        firstName
        lastName
        adoptionNotificationEmail
        fosterNotificationEmail
        preSurrenderNotificationEmail
        dogNotificationEmail
        catNotificationEmail
        taskNotificationsEmail
        inviteDate
        lastLogin
        lastTaskAction
      }
    }
  }
`;

export const REINVITE_AWO_USER = gql`
  mutation ReinviteAwoUser($id: String!, $awoUser: AwoUserInput!) {
    response: reinviteAwoUser(id: $id, awoUser: $awoUser) {
      code
      success
      message
      awoUser {
        id
        email
        firstName
        lastName
        adoptionNotificationEmail
        fosterNotificationEmail
        preSurrenderNotificationEmail
        taskNotificationsEmail
        inviteDate
        lastLogin
      }
    }
  }
`;

export const DELETE_AWO_USER = gql`
  mutation DeleteAwoUser($id: String!, $awoName: String!) {
    response: deleteAwoUser(id: $id, awoName: $awoName) {
      code
      success
      message
      awoUser {
        id
        email
        firstName
        lastName
        adoptionNotificationEmail
        fosterNotificationEmail
        preSurrenderNotificationEmail
        taskNotificationsEmail
        inviteDate
        lastLogin
      }
    }
  }
`;

export const UPDATE_MESSAGE_TEMPLATE = gql`
  mutation UpdateMessageTemplate($id: String!, $messageTemplate: MessageTemplateInput!) {
    response: updateMessageTemplate(id: $id, messageTemplate: $messageTemplate) {
      code
      success
      message
      messageTemplate {
        id
        text
        position
        sendDelay
        note
        numericalResponse
      }
    }
  }
`;

export const CREATE_MESSAGE_TEMPLATE = gql`
  mutation CreateMessageTemplate($messageTemplate: MessageTemplateInput!) {
    response: createMessageTemplate(messageTemplate: $messageTemplate) {
      code
      success
      message
      messageTemplate {
        id
        text
        position
        sendDelay
        note
        numericalResponse
      }
    }
  }
`;

export const DELETE_MESSAGE_TEMPLATE = gql`
  mutation DeleteMessageTemplate($id: String!) {
    response: deleteMessageTemplate(id: $id) {
      code
      success
      message
    }
  }
`;

export const UPDATE_GLOBAL_SETTINGS = gql`
  mutation UpdateGlobalSettings($hourToSend: String!) {
    message: updateGlobalSettings(hourToSend: $hourToSend)
  }
`;

export const UPDATE_TICKET = gql`
  mutation UpdateTicket($id: String!, $ticket: TicketInput!) {
    response: updateTicket(id: $id, ticket: $ticket) {
      code
      success
      message
    }
  }
`;

export const GET_USER = gql`
  query GetDashboard {
    taskCount: taskCount(completed: false) {
      count
    }
    newsfeedCount {
      count
    }
  }
`;

export const GET_MESSAGES = gql`
  query GetMessages($petParentId: String!) {
    messages(petParentId: $petParentId) {
      id
      dateSent
      text
      direction
    }
  }
`;

export const GET_ALBUM_TYPEFORM = gql`
  query GetAlbumTypeform($typeformFormId: String!, $ppId: String!, $animalId: String!) {
    albumForm: dbTypeformForm(id: $typeformFormId) {
      id
      typeformTitle
      taskTitle
      responseTitle
      questions {
        id
        typeformId
        title
        fieldType
        hidden
      }
    }
    responses: response(
      ppId: $ppId,
      typeformIds: [$typeformFormId],
      animalId: $animalId,
      orderBy: "asc"
    ) {
      id
      submittedAt
      answers {
        id
        fieldId
        fieldTypeformId
        fieldType
        text
        question
      }
    }
  }
`;

export const GET_ALBUMS_TYPEFORM_PROGRESS = gql`
  query GetAlbumsTypeformProgress($typeformFormId: String!, $ppId: String!) {
    albumForm: dbTypeformForm(id: $typeformFormId) {
      id
      questions {
        id
        hidden
      }
    }
    responses: response(
      ppId: $ppId,
      typeformIds: [$typeformFormId],
      orderBy: "asc"
    ) {
      id
      animal {
        id
      }
      answers {
        id
        fieldId
      }
    }
  }
`;

export const GET_SHARED_ALBUM_TYPEFORM = gql`
  query GetSharedAlbumTypeform($typeformFormId: String!, $animalId: String!) {
    sharedAlbumTypeform(typeformFormId: $typeformFormId, animalId: $animalId) {
      animal {
        id
        name
        animalType
      }
      albumForm {
        id
        questions {
          id
          typeformId
          title
          fieldType
          hidden
        }
      }
      responses {
        id
        submittedAt
        answers {
          id
          fieldId
          fieldType
          text
          question
        }
      }
    }
  }
`;

export const DELETE_ALBUM_PROMPT_RESPONSE = gql`
  mutation DeleteAlbumPromptResponse($animalId: String!, $typeformFormId: String!, $fieldId: String!) {
    deleteAlbumPromptResponse(
      animalId: $animalId,
      typeformFormId: $typeformFormId,
      fieldId: $fieldId
    )
  }
`;

export const GET_IS_AUTHENTICATED = gql`
  query GetIsAuthenticated($token: String!) {
    isAuthenticated(token: $token)
  }
`;

export const CREATE_SIGN_IN = gql`
  mutation CreateSignIn($email: String!) {
    message: createSignIn(email: $email)
  }
`;

export const CREATE_TOKEN_SIGN_IN = gql`
  mutation CreateTokenSignIn($token: String!, $platform: String!) {
    message: createTokenSignIn(token: $token, platform: $platform)
  }
`;

export const GENERATE_MEDICAL_RECORDS = gql`
  mutation GenerateMedicalRecords {
    message: generateMedicalRecords
  }
`;

export const GET_PET_PHOTOS = gql`
  query GetPetPhotos(
    $awoId: Int, $careType: String, $careTypes: [String!], $petParentId: Int, $limit: Int, $offset: Int
  ) {
    petPhotos(
      awoId: $awoId, careType: $careType, careTypes: $careTypes,
      petParentId: $petParentId, limit: $limit, offset: $offset
    ) {
      photos {
        id
        url
        petName
        petParentName
        petParentId
        createdAt
        exitDate
        careType
        awoName
        likes
        isLikedByCurrentUser
      }
      hasMore
    }
  }
`;

export const LIKE_ANIMAL_MEDIA = gql`
  mutation LikeAnimalMedia($animalMediaId: Int!, $liked: Boolean!) {
    likeAnimalMedia(animalMediaId: $animalMediaId, liked: $liked) {
      success
      likes
      isLikedByCurrentUser
    }
  }
`;

export const GET_EXPERIENCE_TAB = gql`
  query GetExperienceTab(
    $awoId: String!,
    $startDate: String!,
    $endDate: String!
    $typeformIds: [String!],
    $daysAgo: Int,
    $limit: Int,
    $offset: Int,
    $animalType: String,
    $careType: String,
    $orderBy: String,
    $excludeSkipTaskCreation: Boolean = false
  ) {
    npsOverTime(awoId: $awoId, startDate: $startDate, endDate: $endDate) {
      month
      score
      totalResponses
    }
    responses: response(
      typeformIds: $typeformIds,
      awoId: $awoId,
      limit: $limit,
      offset: $offset,
      animalType: $animalType,
      careType: $careType,
      orderBy: $orderBy,
      daysAgo: $daysAgo,
      excludeSkipTaskCreation: $excludeSkipTaskCreation
    ) {
      id
      typeformId
      submittedAt
      title
      petParent {
        id
        email
        firstName
        lastName
        animals {
          id
          name
          animalType
          careType
        }
      }
      answers {
        id
        text
        question
      }
    }
  }
`;

export const GET_BEHAVIOR_CONCERNS_TAB = gql`
  query GetBehaviorConcernsTab($feedbackDaysAgo: Int, $dogTypeformIds: [Int!], $catTypeformIds: [Int!]) {
    awoDogBehavioralIssues: behavioralIssues(
      typeformFormIds: $dogTypeformIds,
      animalType: "Dog",
      invert: 0,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      numResponsesNoFilters
      issues
    }
    awoCatBehavioralIssues: behavioralIssues(
      typeformFormIds: $catTypeformIds,
      animalType: "Cat",
      invert: 0,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      numResponsesNoFilters
      issues
    }
    baselineDogBehavioralIssues: behavioralIssues(
      typeformFormIds: $dogTypeformIds,
      animalType: "Dog",
      invert: 1,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
    baselineCatBehavioralIssues: behavioralIssues(
      typeformFormIds: $catTypeformIds,
      animalType: "Cat",
      invert: 1,
      daysAgo: $feedbackDaysAgo
    ) {
      numResponses
      issues
    }
  }
`;

export const GET_PET_PHOTO = gql`
  query GetPetPhoto($url: String!) {
    petPhoto(url: $url) {
      dataUrl
      contentType
    }
  }
`;

export const GET_HUB_MESSAGE_TIMELINE = gql`
  query GetHubMessageTimeline {
    hubMessageTimeline {
      unreadCount
      membershipStatus
      messages {
        id
        sender
        channel
        text
        html
        sentAt
        survey
        unread
        media {
          type
          src
          poster
          duration
          broken
        }
      }
    }
  }
`;

// Lightweight selection for the nav badge + Home banner. HubMessageTimeline is not
// normalized in the Apollo cache (no id / keyFields), so this shares the same cache
// slot as GET_HUB_MESSAGE_TIMELINE — do not add a typePolicy for it.
export const GET_HUB_UNREAD_COUNT = gql`
  query GetHubUnreadCount {
    hubMessageTimeline {
      unreadCount
    }
  }
`;

export const MARK_HUB_MESSAGES_READ = gql`
  mutation MarkHubMessagesRead($upToSentAt: String) {
    markHubMessagesRead(upToSentAt: $upToSentAt)
  }
`;

// Presign S3 PUT URLs for outbound message attachments. The browser uploads each
// file directly to S3, then sends the returned fileUrls in sendHubMessage.
export const CREATE_HUB_MEDIA_UPLOAD_URLS = gql`
  mutation CreateHubMediaUploadUrls($files: [HubMediaFileInput!]!) {
    createHubMediaUploadUrls(files: $files) {
      uploadUrl
      fileUrl
      key
    }
  }
`;

// Send an outbound message from the Hub. Creates/append a Zendesk ticket server-side
// and returns the persisted message in the same shape as the timeline so it can be
// appended to the GET_HUB_MESSAGE_TIMELINE cache.
export const SEND_HUB_MESSAGE = gql`
  mutation SendHubMessage($text: String, $media: [HubMediaInput!]) {
    sendHubMessage(text: $text, media: $media) {
      id
      sender
      channel
      text
      html
      sentAt
      survey
      unread
      media {
        type
        src
        poster
        duration
        broken
      }
    }
  }
`;

export const SET_ANIMAL_MEDIA_HIDDEN = gql`
  mutation SetAnimalMediaHidden($id: Int!, $hidden: Boolean!) {
    setAnimalMediaHidden(id: $id, hidden: $hidden) {
      id
      hidden
    }
  }
`;
